import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { base44 } from "@/api/base44Client";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function WaterSlides() {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    base44.entities.WaterSlide.list("-sort_order", 20)
      .then((data) => {
        setSlides(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error loading water slides:", err);
        setLoading(false);
      });
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-primary/5 py-12">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            Our Water Slide Collection
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Choose from our exciting selection of inflatable water slides perfect for your next event
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {slides.map((slide, index) => (
              <motion.div
                key={slide.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Link to={`/product/${slide.id}`} className="group block">
                  <div className="bg-card rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 border border-border">
                    <div className="relative aspect-[4/3] overflow-hidden bg-muted">
                      <img
                        src={slide.image_url || "https://via.placeholder.com/600x400"}
                        alt={slide.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      {slide.is_popular && (
                        <div className="absolute top-4 right-4 bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm font-bold">
                          Popular
                        </div>
                      )}
                    </div>
                    
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-foreground mb-2 group-hover:text-primary transition-colors">
                        {slide.name}
                      </h3>
                      <p className="text-muted-foreground mb-4 line-clamp-2">
                        {slide.description}
                      </p>
                      
                      <div className="flex items-center justify-between">
                        <div className="text-2xl font-bold text-primary">
                          {slide.price || "$199"}<span className="text-sm font-normal text-muted-foreground">/day</span>
                        </div>
                        <div className="flex items-center gap-2 text-primary font-medium group-hover:gap-3 transition-all">
                          Book Now
                          <ArrowRight className="w-4 h-4" />
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}

        {slides.length === 0 && !loading && (
          <div className="text-center py-20">
            <p className="text-muted-foreground mb-4">No water slides available at the moment</p>
            <Link to="/">
              <Button>Back to Home</Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}